// filepath: holl-client-management.component.ts
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Client, NIABioData } from './client.model';
import { ClientService } from './client.service';
import { ExportService } from '../service/quote-export.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-holl-client-management',
  templateUrl: './holl-client-management.component.html',
  styleUrls: ['./holl-client-management.component.scss']
})
export class HollClientManagementComponent implements OnInit, OnDestroy {

  // ── View state ───────────────────────────────────────────
  viewMode: 'list' | 'detail' | 'onboarding' = 'list';
  clients: Client[] = [];
  filteredClients: Client[] = [];
  selectedClient: Client | null = null;
  searchQuery = '';
  selectedStatusFilter = '';
  isLoading = false;

  // ── Export dropdown ────────────────────────────────────
  showExportMenu: boolean = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const wrap = this.elRef.nativeElement.querySelector('.export-wrap');
    if (this.showExportMenu && wrap && !wrap.contains(event.target)) {
      this.showExportMenu = false;
    }
  }

  // Pagination
  currentPage = 0;
  pageSize = 5;
  totalItems = 0;
  totalPages = 0;

  // ── Onboarding stepper ───────────────────────────────────
  currentStep = 1; // 1 = NIA Verification, 2 = Bio Information
  isVerifying = false;
  verificationSuccess = false;
  verificationSkipped = false;
  verificationError = '';

  // Step 1: NIA fields
  ghCardNumber = '';
  selfieMode: 'upload' | 'camera' = 'upload';
  selfieBase64 = '';
  selfieFileName = '';

  // Camera
  @ViewChild('videoElement') videoElement: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement: ElementRef<HTMLCanvasElement>;
  cameraStream: MediaStream | null = null;
  cameraActive = false;

  // Step 2: Bio data (pre-populated from NIA)
  bioData: NIABioData | null = null;
  clientForm: Client = this.getEmptyClient();
  formErrors: { [key: string]: string } = {};
  formTouched: { [key: string]: boolean } = {};

  preferredContactOptions = [
    { value: 'phone', label: 'Phone Call' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' }
  ];

  constructor(
    private router: Router,
    private clientService: ClientService,
    private exportService: ExportService,
    private elRef: ElementRef
  ) {}

  ngOnInit() {
    this.loadClients();
  }

  ngOnDestroy() {
    this.stopCamera();
  }

  getEmptyClient(): Client {
    return {
      surname: '',
      otherNames: '',
      dateOfBirth: '',
      occupation: '',
      telephone: '',
      email: '',
      preferredContact: 'phone',
      ghCardNumber: '',
      postalAddress: '',
      digitalAddress: '',
      cellNumber: ''
    };
  }

  // ════════════════════════════════════════════════════════════
  // LIST VIEW
  // ════════════════════════════════════════════════════════════
  loadClients() {
    this.isLoading = true;
    this.clientService.getAllClients(
      this.currentPage, this.pageSize, null, this.selectedStatusFilter || null
    ).subscribe(
      (response) => {
        if (response.responseCode === '000') {
          this.clients = response.data.clients || [];
          this.totalItems = response.data.totalItems;
          this.totalPages = response.data.totalPages;
          this.currentPage = response.data.currentPage;
          this.filteredClients = this.clients.slice();
        }
        this.isLoading = false;
      },
      (error) => {
        console.error('Error loading clients:', error);
        this.isLoading = false;
      }
    );
  }

  onSearch() {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredClients = this.clients.slice();
      return;
    }
    this.filteredClients = this.clients.filter(c =>
      (c.fullName && c.fullName.toLowerCase().includes(q)) ||
      c.surname.toLowerCase().includes(q) ||
      c.otherNames.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      c.telephone.includes(q) ||
      c.ghCardNumber.toLowerCase().includes(q)
    );
  }

  onStatusFilterChange() {
    this.currentPage = 0;
    this.loadClients();
  }

  viewClient(client: Client) {
    this.selectedClient = Object.assign({}, client);
    this.editingSection = null;
    this.viewMode = 'detail';
  }

  deleteClient(client: Client) {
    Swal.fire({
      title: 'Delete Client?',
      text: `Are you sure you want to delete "${client.fullName || client.surname}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F05B24',
      cancelButtonColor: '#a0a0b0',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.value) {
        this.isLoading = true;
        this.clientService.deleteClient(client.clientId).subscribe(
          (response) => {
            this.isLoading = false;
            if (response.responseCode === '000') {
              Swal.fire({ title: 'Deleted!', text: `"${client.fullName}" has been removed.`, icon: 'success' });
              this.loadClients();
            }
          },
          (error) => {
            this.isLoading = false;
            Swal.fire({ title: 'Error', text: 'Unable to delete client.', icon: 'error' });
          }
        );
      }
    });
  }

  backToList() {
    this.viewMode = 'list';
    this.selectedClient = null;
    this.editingSection = null;
    this.resetOnboarding();
  }

  // ── Pagination ─────────────────────────────────────────────
  goToPage(page: number) {
    if (page < 0 || page >= this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadClients();
  }

  nextPage() { this.goToPage(this.currentPage + 1); }
  prevPage() { this.goToPage(this.currentPage - 1); }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(0, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible);
    if (end - start < maxVisible) start = Math.max(0, end - maxVisible);
    for (let i = start; i < end; i++) pages.push(i);
    return pages;
  }

  // ════════════════════════════════════════════════════════════
  // ONBOARDING STEPPER
  // ════════════════════════════════════════════════════════════
  startOnboarding() {
    this.resetOnboarding();
    this.viewMode = 'onboarding';
    this.currentStep = 1;
  }

  resetOnboarding() {
    this.currentStep = 1;
    this.ghCardNumber = '';
    this.selfieBase64 = '';
    this.selfieFileName = '';
    this.selfieMode = 'upload';
    this.verificationSuccess = false;
    this.verificationSkipped = false;
    this.verificationError = '';
    this.isVerifying = false;
    this.bioData = null;
    this.clientForm = this.getEmptyClient();
    this.formErrors = {};
    this.formTouched = {};
    this.stopCamera();
  }

  // ── Step 1: Photo upload ──────────────────────────────────
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selfieFileName = file.name;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selfieBase64 = e.target.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  removeSelfie() {
    this.selfieBase64 = '';
    this.selfieFileName = '';
  }

  // ── Step 1: Live camera ───────────────────────────────────
  switchToCamera() {
    this.selfieMode = 'camera';
    this.startCamera();
  }

  switchToUpload() {
    this.selfieMode = 'upload';
    this.stopCamera();
  }

  async startCamera() {
    try {
      this.cameraStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
      });
      this.cameraActive = true;

      setTimeout(() => {
        if (this.videoElement && this.videoElement.nativeElement) {
          this.videoElement.nativeElement.srcObject = this.cameraStream;
          this.videoElement.nativeElement.play();
        }
      }, 100);
    } catch (err) {
      console.error('Camera access denied:', err);
      Swal.fire({
        title: 'Camera Error',
        text: 'Unable to access camera. Please check permissions or use the upload option.',
        icon: 'error'
      });
      this.selfieMode = 'upload';
    }
  }

  stopCamera() {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => track.stop());
      this.cameraStream = null;
    }
    this.cameraActive = false;
  }

  capturePhoto() {
    if (!this.videoElement || !this.canvasElement) return;

    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    // Mirror the image
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    this.selfieBase64 = canvas.toDataURL('image/jpeg', 0.8);
    this.selfieFileName = 'live-capture.jpg';
    this.stopCamera();
    this.selfieMode = 'upload'; // switch back to show the captured image
  }

  // ── Step 1: Verify ────────────────────────────────────────
  canVerify(): boolean {
    return !!(this.ghCardNumber && this.ghCardNumber.trim() && this.selfieBase64);
  }

  verifyNIA() {
    if (!this.canVerify()) return;

    this.isVerifying = true;
    this.verificationError = '';

    this.clientService.verifyWithNIA(this.ghCardNumber, this.selfieBase64).subscribe(
      (result) => {
        this.isVerifying = false;
        if (result.success && result.bioData) {
          this.verificationSuccess = true;
          this.verificationSkipped = false;
          this.bioData = result.bioData;
          this.populateFormFromBio(result.bioData);

          Swal.fire({
            title: 'Verification Successful!',
            text: 'Identity verified. Bio data has been retrieved.',
            icon: 'success'
          });

          // Auto-advance to step 2 after a brief delay
          setTimeout(() => {
            this.currentStep = 2;
          }, 1500);
        } else {
          this.verificationError = result.errorMessage || 'NIA verification failed. Please try again.';
        }
      },
      (error) => {
        this.isVerifying = false;
        this.verificationError = 'Verification service unavailable. Please try again later.';
      }
    );
  }

  skipNIA() {
    Swal.fire({
      title: 'Skip NIA Verification?',
      text: 'You will need to enter the client\'s bio information manually instead of pulling it from NIA.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F05B24',
      cancelButtonColor: '#a0a0b0',
      confirmButtonText: 'Yes, skip',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.value) {
        this.verificationSkipped = true;
        this.verificationError = '';
        this.currentStep = 2;
      }
    });
  }

  populateFormFromBio(bio: NIABioData) {
    this.clientForm = {
      surname: bio.surname,
      otherNames: bio.otherNames,
      dateOfBirth: bio.dateOfBirth,
      occupation: bio.occupation,
      telephone: bio.telephone,
      email: bio.email,
      preferredContact: 'phone',
      ghCardNumber: bio.ghCardNumber,
      postalAddress: bio.postalAddress,
      digitalAddress: bio.digitalAddress,
      cellNumber: bio.telephone,
      gender: bio.gender,
      nationality: bio.nationality
    };
  }

  goToStep(step: number) {
    if (step === 2 && !this.verificationSuccess && !this.verificationSkipped) return;
    this.currentStep = step;
  }

  // ── Step 2: Validation ────────────────────────────────────
  markTouched(field: string) {
    this.formTouched[field] = true;
    this.validateField(field);
  }

  validateField(field: string) {
    const val = this.clientForm[field];

    switch (field) {
      case 'surname':
        if (!val || !val.trim()) this.formErrors[field] = 'Surname is required.';
        else delete this.formErrors[field];
        break;
      case 'otherNames':
        if (!val || !val.trim()) this.formErrors[field] = 'Other names are required.';
        else delete this.formErrors[field];
        break;
      case 'telephone':
        if (!val || !val.trim()) this.formErrors[field] = 'Telephone is required.';
        else delete this.formErrors[field];
        break;
      case 'email':
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!val || !val.trim()) this.formErrors[field] = 'Email is required.';
        else if (!emailRegex.test(val.trim())) this.formErrors[field] = 'Enter a valid email.';
        else delete this.formErrors[field];
        break;
      case 'preferredContact':
        if (!val || !val.trim()) this.formErrors[field] = 'Preferred contact is required.';
        else delete this.formErrors[field];
        break;
      case 'cellNumber':
        if (!val || !val.trim()) this.formErrors[field] = 'Cell number is required.';
        else delete this.formErrors[field];
        break;
      default:
        delete this.formErrors[field];
    }
  }

  validateAll(): boolean {
    const fields = ['surname', 'otherNames', 'telephone', 'email', 'preferredContact', 'cellNumber'];
    fields.forEach(f => {
      this.formTouched[f] = true;
      this.validateField(f);
    });
    return Object.keys(this.formErrors).length === 0;
  }

  hasError(field: string): boolean {
    return this.formTouched[field] && !!this.formErrors[field];
  }

  // ── Step 2: Save ──────────────────────────────────────────
  saveClient() {
    if (!this.validateAll()) return;

    this.isLoading = true;
    this.clientService.createClient(this.clientForm).subscribe(
      (response) => {
        this.isLoading = false;
        if (response.responseCode === '000') {
          Swal.fire({
            title: 'Client Onboarded!',
            text: `${this.clientForm.otherNames} ${this.clientForm.surname} has been successfully registered.`,
            icon: 'success'
          });
          this.viewMode = 'list';
          this.resetOnboarding();
          this.loadClients();
        } else {
          Swal.fire({ title: 'Error', text: response.responseMessage || 'Unable to save client. Please try again.', icon: 'error' });
        }
      },
      (error) => {
        this.isLoading = false;
        const msg = (error && error.error && error.error.responseMessage) || 'Unable to save client. Please try again.';
        Swal.fire({ title: 'Error', text: msg, icon: 'error' });
      }
    );
  }

  // ════════════════════════════════════════════════════════════
  // DETAIL VIEW — EDIT
  // ════════════════════════════════════════════════════════════
  editingSection: 'personal' | 'contact' | 'idVerification' | null = null;
  editForm: Client = this.getEmptyClient();
  editFormErrors: { [key: string]: string } = {};
  editFormTouched: { [key: string]: boolean } = {};
  isSavingEdit = false;

  /** GH Card number at the time the user opened idVerification edit */
  private editGhCardOriginal = '';

  /** Selfie for re-verification (only used in idVerification section) */
  editSelfieBase64 = '';
  editSelfieFileName = '';
  editVerifying = false;
  editVerificationError = '';
  editVerified = false; // true after a successful re-verify (or card unchanged)

  startEditing(section: 'personal' | 'contact' | 'idVerification') {
    this.editingSection = section;
    // Deep copy so changes don't mutate selectedClient until saved
    this.editForm = Object.assign({}, this.selectedClient);
    this.editFormErrors = {};
    this.editFormTouched = {};
    this.editVerificationError = '';
    this.editVerified = false;
    this.editSelfieBase64 = '';
    this.editSelfieFileName = '';

    if (section === 'idVerification') {
      this.editGhCardOriginal = this.selectedClient.ghCardNumber;
    }
  }

  cancelEdit() {
    this.editingSection = null;
    this.editFormErrors = {};
    this.editFormTouched = {};
    this.editVerificationError = '';
    this.editVerified = false;
    this.editSelfieBase64 = '';
    this.editSelfieFileName = '';
  }

  markEditTouched(field: string) {
    this.editFormTouched[field] = true;
    this.validateEditField(field);
  }

  validateEditField(field: string) {
    const val = this.editForm[field];
    switch (field) {
      case 'surname':
        if (!val || !val.trim()) this.editFormErrors[field] = 'Surname is required.';
        else delete this.editFormErrors[field];
        break;
      case 'otherNames':
        if (!val || !val.trim()) this.editFormErrors[field] = 'Other names are required.';
        else delete this.editFormErrors[field];
        break;
      case 'telephone':
        if (!val || !val.trim()) this.editFormErrors[field] = 'Telephone is required.';
        else delete this.editFormErrors[field];
        break;
      case 'email': {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!val || !val.trim()) this.editFormErrors[field] = 'Email is required.';
        else if (!emailRegex.test(val.trim())) this.editFormErrors[field] = 'Enter a valid email.';
        else delete this.editFormErrors[field];
        break;
      }
      case 'cellNumber':
        if (!val || !val.trim()) this.editFormErrors[field] = 'Cell number is required.';
        else delete this.editFormErrors[field];
        break;
      case 'ghCardNumber':
        if (!val || !val.trim()) this.editFormErrors[field] = 'GH Card number is required.';
        else delete this.editFormErrors[field];
        break;
      default:
        delete this.editFormErrors[field];
    }
  }

  hasEditError(field: string): boolean {
    return this.editFormTouched[field] && !!this.editFormErrors[field];
  }

  /** Whether the GH Card has been changed from its original value */
  get ghCardChanged(): boolean {
    return this.editForm.ghCardNumber !== this.editGhCardOriginal;
  }

  /** Whether the current section's edit can be saved */
  canSaveEdit(): boolean {
    if (this.editingSection === 'idVerification') {
      // If card changed, require a fresh re-verification; otherwise allow save directly
      if (this.ghCardChanged) {
        return this.editVerified;
      }
    }
    return true;
  }

  onEditSelfieSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.editSelfieFileName = file.name;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.editSelfieBase64 = e.target.result as string;
        this.editVerified = false;
        this.editVerificationError = '';
      };
      reader.readAsDataURL(file);
    }
  }

  removeEditSelfie() {
    this.editSelfieBase64 = '';
    this.editSelfieFileName = '';
    this.editVerified = false;
  }

  reVerifyNIA() {
    if (!this.editForm.ghCardNumber || !this.editSelfieBase64) return;

    this.editVerifying = true;
    this.editVerificationError = '';
    this.editVerified = false;

    this.clientService.verifyWithNIA(this.editForm.ghCardNumber, this.editSelfieBase64).subscribe(
      (result) => {
        this.editVerifying = false;
        if (result.success) {
          this.editVerified = true;
          this.editVerificationError = '';
          // Optionally update verificationStatus on the form
          this.editForm = { ...this.editForm, verificationStatus: 'Verified' };
        } else {
          this.editVerificationError = result.errorMessage || 'Re-verification failed. Please try again.';
        }
      },
      () => {
        this.editVerifying = false;
        this.editVerificationError = 'Verification service unavailable. Please try again later.';
      }
    );
  }

  private getEditFieldsForSection(): string[] {
    switch (this.editingSection) {
      case 'personal':       return ['surname', 'otherNames'];
      case 'contact':        return ['cellNumber'];  // email & telephone are locked, not validated
      case 'idVerification': return ['ghCardNumber'];
      default: return [];
    }
  }

  saveEdit() {
    // Validate required fields for this section
    const fields = this.getEditFieldsForSection();
    fields.forEach(f => {
      this.editFormTouched[f] = true;
      this.validateEditField(f);
    });
    const hasErrors = fields.some(f => !!this.editFormErrors[f]);
    if (hasErrors) return;

    if (!this.canSaveEdit()) {
      if (this.editingSection === 'idVerification' && this.ghCardChanged) {
        this.editVerificationError = 'Please re-verify the new GH Card number before saving.';
      }
      return;
    }

    this.isSavingEdit = true;

    // Build only the changed fields for this section.
    // email and telephone are always included as the original locked values
    // so the API can identify the record — they are never changed.
    const changes: Partial<Client> = {
      email: this.selectedClient.email,
      telephone: this.selectedClient.telephone
    };
    switch (this.editingSection) {
      case 'personal':
        changes.surname = this.editForm.surname;
        changes.otherNames = this.editForm.otherNames;
        changes.dateOfBirth = this.editForm.dateOfBirth;
        changes.occupation = this.editForm.occupation;
        changes.gender = this.editForm.gender;
        changes.nationality = this.editForm.nationality;
        break;
      case 'contact':
        // telephone is already set above as the locked value;
        // only cellNumber, preferredContact and address fields are editable
        changes.cellNumber = this.editForm.cellNumber;
        changes.preferredContact = this.editForm.preferredContact;
        changes.postalAddress = this.editForm.postalAddress;
        changes.digitalAddress = this.editForm.digitalAddress;
        break;
      case 'idVerification':
        changes.ghCardNumber = this.editForm.ghCardNumber;
        if (this.ghCardChanged) {
          changes.verificationStatus = 'Verified';
        }
        break;
    }

    this.clientService.updateClient(this.selectedClient.clientId, changes).subscribe(
      (response) => {
        this.isSavingEdit = false;
        if (response.responseCode === '000') {
          // Merge changes back into selectedClient
          this.selectedClient = Object.assign({}, this.selectedClient, changes, {
            fullName: `${changes.otherNames || this.selectedClient.otherNames} ${changes.surname || this.selectedClient.surname}`,
            modifiedAt: new Date().toISOString()
          });
          this.cancelEdit();

          Swal.fire({
            title: 'Saved!',
            text: 'Client information has been updated successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          this.loadClients(); // refresh the list in the background
        } else {
          Swal.fire({ title: 'Error', text: 'Failed to save changes. Please try again.', icon: 'error' });
        }
      },
      () => {
        this.isSavingEdit = false;
        Swal.fire({ title: 'Error', text: 'Unable to save changes. Please try again.', icon: 'error' });
      }
    );
  }

  // ── Export ────────────────────────────────────────────────
  toggleExportMenu() { this.showExportMenu = !this.showExportMenu; }

  exportAs(format: 'excel' | 'csv' | 'pdf') {
    this.showExportMenu = false;
    const data = this.filteredClients.length ? this.filteredClients : this.clients;
    if (!data || data.length === 0) { return; }
    const filename = 'client_management_' + new Date().toISOString().slice(0, 10);
    this.exportService.export(format, ExportService.CLIENTS, data, filename);
  }

  // ── Helpers ───────────────────────────────────────────────
  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].substring(0, 2).toUpperCase();
  }

  formatDate(dateStr: string, includeTime: boolean = false): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    if (!includeTime) return datePart;
    const timePart = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  }

  getStatusClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'active': return 'status-active';
      case 'pending': return 'status-pending';
      case 'inactive': return 'status-inactive';
      default: return 'status-inactive';
    }
  }

  getVerificationBadgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'verified': return 'badge-verified';
      case 'pending': return 'badge-pending';
      case 'failed': return 'badge-failed';
      default: return 'badge-default';
    }
  }
}
